import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, Typography, Input, Textarea, Button, FormControl, FormLabel,
  Switch, Chip, CircularProgress, Stack,
  Tabs, TabList, Tab, TabPanel,
} from '@mui/joy';
import { motion } from 'framer-motion';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { adminApi } from './adminApi';
import { markdownComponents } from './markdownComponents';
import { useBlogChat } from './useBlogChat';
import { BlogChatPanel } from './BlogChatPanel';
import { DiffViewer } from './DiffViewer';

const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const today = () => new Date().toISOString().split('T')[0];

const deriveTitleFromDraft = (draft, fallback = '') => {
  if (!draft) return fallback || '';
  const lines = draft.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
  // Prefer first heading
  let t = lines.find((l) => /^#+\s/.test(l)) || '';
  if (!t) {
    // fallback to first substantial line that is not a list or code fence
    t = lines.find((l) => !/^[-*+]\s|^\d+\.\s|^```/.test(l)) || lines[0] || '';
  }
  t = t.replace(/^#+\s+/, '').replace(/[*_`~]/g, '').trim();
  // Take up to first sentence if too long
  if (t.length > 80) {
    const sent = t.split(/[.!?:]/)[0].trim();
    if (sent.length > 10) t = sent;
  }
  if (t.length < 6 && fallback) return fallback;
  if (t.length > 90) t = t.slice(0, 87).trim() + '…';
  return t || fallback || '';
};

const deriveExcerptFromDraft = (draft) => {
  if (!draft) return '';
  let text = draft
    .replace(/^#{1,6}\s.*$/gm, '')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`[^`]+`/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
  // Join into paragraphs
  const paras = text.split(/\n{2,}/).map((p) => p.replace(/\s+/g, ' ').trim()).filter((p) => p.length > 20);
  let ex = paras[0] || text.slice(0, 320);
  // Keep it to ~1-2 sentences
  const firstSentEnd = ex.search(/[.!?]\s/);
  if (firstSentEnd > 60 && firstSentEnd < 280) {
    ex = ex.slice(0, firstSentEnd + 1);
  }
  ex = ex.replace(/\s+/g, ' ').trim();
  if (ex.length > 260) ex = ex.slice(0, 257).trim() + '…';
  return ex;
};

// No longer used for direct application (we derive inline), but kept for reference.
// applyDraftToEmptyMetadata was removed — autofill logic is inlined for clarity and to guarantee "only if empty".

// Helpers to ensure chat only ever stores short summaries, while full drafts go to preview/diff
const CHAT_SUMMARY_START = '---CHAT-SUMMARY---';
const CHAT_SUMMARY_END = '---END-CHAT-SUMMARY---';

const splitDraftAndSummary = (text = '') => {
  if (!text) return { full: '', summary: '', suggestedTitle: '', suggestedExcerpt: '', suggestedTags: [] };
  const startIdx = text.indexOf(CHAT_SUMMARY_START);
  if (startIdx === -1) {
    if (text.length > 1200) {
      const teaser = text.split('\n').slice(0, 2).join(' ').slice(0, 280) + '…';
      return { full: text, summary: `${teaser} [See Differences tab for full draft]`, suggestedTitle: '', suggestedExcerpt: '', suggestedTags: [] };
    }
    return { full: text, summary: text, suggestedTitle: '', suggestedExcerpt: '', suggestedTags: [] };
  }
  const full = text.slice(0, startIdx).trim();
  let rest = text.slice(startIdx);
  const endIdx = rest.indexOf(CHAT_SUMMARY_END);
  let summaryBlock = rest;
  if (endIdx !== -1) summaryBlock = rest.slice(0, endIdx + CHAT_SUMMARY_END.length);
  const summary = summaryBlock
    .replace(CHAT_SUMMARY_START, '')
    .replace(CHAT_SUMMARY_END, '')
    .trim();

  // Parse explicit suggestions from the summary block (model-provided)
  const tMatch = summaryBlock.match(/Suggested title:\s*(.+?)(?:\n|$)/i);
  const eMatch = summaryBlock.match(/Suggested excerpt:\s*(.+?)(?:\n|$)/i);
  const tagsMatch = summaryBlock.match(/Suggested tags?:\s*([^\n]+)/i);
  let suggestedTitle = tMatch ? tMatch[1].trim() : '';
  let suggestedExcerpt = eMatch ? eMatch[1].trim() : '';
  let suggestedTags = [];
  if (tagsMatch) {
    suggestedTags = tagsMatch[1]
      .split(/[,;]/)
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 10);
  }
  if (suggestedTitle.length > 90) suggestedTitle = suggestedTitle.slice(0, 87).trim() + '…';
  if (suggestedExcerpt.length > 260) suggestedExcerpt = suggestedExcerpt.slice(0, 257).trim() + '…';

  return { full: full || text, summary: summary || rest, suggestedTitle, suggestedExcerpt, suggestedTags };
};

const sanitizeMessagesForPersistence = (msgs = []) =>
  msgs
    // Drop empty/whitespace-only messages (e.g. the streaming assistant placeholder before
    // any delta text has arrived). The backend's ChatMessage.content requires min_length=1,
    // and persisting mid-stream (e.g. from a "patch" event that fires before deltas) can
    // otherwise send an empty placeholder and trigger a 422 on /authoring-sessions.
    .filter((m) => typeof m.content === 'string' && m.content.trim().length > 0)
    .map((m) => {
      if (m.role !== 'assistant' || typeof m.content !== 'string') return m;
      if (m.content.includes(CHAT_SUMMARY_START) || m.content.length > 1200) {
        const { summary } = splitDraftAndSummary(m.content);
        return { ...m, content: summary || m.content.slice(0, 350) };
      }
      return m;
    })
    // Guard again after summarization in case splitDraftAndSummary ever yields an empty summary
    .filter((m) => typeof m.content === 'string' && m.content.trim().length > 0);


const AdminBlogEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  // AI authoring state
  const [outline, setOutline] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [proposedDraft, setProposedDraft] = useState(null);
  const [, setPersistingSession] = useState(false); // reserved for future saving indicator (persisted via authoring session)

  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    tags: [],
    author: 'Calvet Technology Solutions',
    date: today(),
    published: false,
  });
  const [tagInput, setTagInput] = useState('');

  // Chat — every turn (free text or a button's predefined prompt) is routed through the
  // backend planner via onPatch, which applies structured UI updates (metadata/research)
  // immediately and deterministically (no regex-parsing of chat text needed for those).
  const handlePatchRef = useRef(() => {});
  const chat = useBlogChat([], { onPatch: (target, data) => handlePatchRef.current(target, data) });
  const [activeStage, setActiveStage] = useState(null);

  // 5-tab UI (laptop admin only)
  const [activeTab, setActiveTab] = useState(0); // 0=Chat, 1=Metadata Form, 2=Preview, 3=Diff, 4=Research

  // Track suggested metadata parsed from the last AI draft's chat-summary block.
  // Used only to autofill fields that are STILL EMPTY. Never overwrite user values.
  const [lastSuggestedTitle, setLastSuggestedTitle] = useState('');
  const [lastSuggestedExcerpt, setLastSuggestedExcerpt] = useState('');
  const [lastSuggestedTags, setLastSuggestedTags] = useState([]);

  // Per-article research sources (type: ResearchSource) captured via DuckDuckGo (US region).
  // Automatically saved to the authoring session so they can be referenced later while writing the article.
  const [researchSources, setResearchSources] = useState([]);
  const [researchMarkdown, setResearchMarkdown] = useState('');

  // Persist (or create) the AI authoring session
  // IMPORTANT: chat messages persisted here must be SHORT summaries only.
  // Full article drafts are kept in proposedDraft / form.content and sent via last_proposed_draft when needed.
  // Declared early to avoid TDZ when referenced in useEffect deps below
  const persistCurrentSession = useCallback(async (extra = {}) => {
    const currentMsgs = chat.messages || [];
    if (currentMsgs.length === 0 && !extra.research_sources && !extra.last_research_markdown) return;
    setPersistingSession(true);
    try {
      // Always sanitize messages to short summaries to prevent 413 on authoring-sessions
      const messagesForSave = extra.messages
        ? sanitizeMessagesForPersistence(extra.messages)
        : sanitizeMessagesForPersistence(currentMsgs);

      const payload = {
        linked_post_id: id || null,
        topic: form.title || (messagesForSave[0]?.content || '').slice(0, 200),
        outline,
        messages: messagesForSave,
        last_proposed_draft: extra.last_proposed_draft ?? undefined,
        // Per-article research sources (type ResearchSource) — automatically saved for later reference while writing
        research_sources: extra.research_sources ?? undefined,
        last_research_markdown: extra.last_research_markdown ?? undefined,
        ...extra,
      };

      if (sessionId) {
        await adminApi.put(`/authoring-sessions/${sessionId}`, payload);
      } else {
        const { data } = await adminApi.post('/authoring-sessions', payload);
        if (data?.id) setSessionId(data.id);
      }
    } catch (e) {
      // non-fatal for UX
      console.warn('Failed to persist authoring session', e);
    } finally {
      setPersistingSession(false);
    }
  }, [chat.messages, form.title, id, outline, sessionId]);

  // Grounding context sent to the backend planner + agents on every turn (free text or a
  // button's predefined prompt). Kept up to date via closure — reassigned each render.
  const buildContext = (extra = {}) => ({
    topic: form.title || chat.messages.find((m) => m.role === 'user')?.content || '',
    outline,
    content: form.content,
    title: form.title,
    slug: form.slug,
    excerpt: form.excerpt,
    tags: form.tags,
    ...extra,
  });

  // Applies structured "patch" events emitted by the backend (metadata / research agents).
  // These are immediate, deterministic UI updates — no regex-parsing of chat text needed.
  // Reassigned on every render so closures (form, sessionId, etc.) stay fresh.
  handlePatchRef.current = (target, data = {}) => {
    if (target === 'metadata') {
      setForm((prev) => {
        const next = { ...prev };
        if (typeof data.title === 'string') next.title = data.title;
        if (typeof data.slug === 'string' && data.slug) next.slug = data.slug;
        if (typeof data.excerpt === 'string') next.excerpt = data.excerpt;
        if (Array.isArray(data.tags)) next.tags = data.tags;
        return next;
      });
      if (typeof data.slug === 'string' && data.slug) setSlugTouched(true);
      const changed = Object.keys(data);
      toast.success(changed.length ? `Metadata updated: ${changed.join(', ')}` : 'Metadata updated');
      return;
    }
    if (target === 'research') {
      const sources = Array.isArray(data.sources) ? data.sources : [];
      const markdown = data.markdown || '';
      setResearchSources(sources);
      setResearchMarkdown(markdown);
      persistCurrentSession({ research_sources: sources, last_research_markdown: markdown });
      toast.success(`Live research complete — ${sources.length} source${sources.length === 1 ? '' : 's'} captured (US). See the Research tab.`);
    }
  };

  // Load post + linked authoring session (for resume)
  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        const { data } = await adminApi.get(`/blogs/${id}`);
        setForm({
          title: data.title,
          slug: data.slug,
          excerpt: data.excerpt,
          content: data.content,
          tags: data.tags || [],
          author: data.author,
          date: data.date,
          published: data.published,
        });
        setSlugTouched(true);

        // Load AI authoring session if present
        try {
          const { data: sess } = await adminApi.get(`/authoring-sessions/by-post/${id}`);
          if (sess) {
            setSessionId(sess.id);
            if (Array.isArray(sess.outline)) setOutline(sess.outline);
            if (Array.isArray(sess.messages) && sess.messages.length) {
              // Sanitize: old sessions may contain full drafts inside messages; keep only summaries
              chat.setMessages(sanitizeMessagesForPersistence(sess.messages));
            }
            if (sess.last_proposed_draft) {
              setProposedDraft(sess.last_proposed_draft);

              // Autofill empty metadata from restored draft (only if still empty).
              // Also parse the last assistant message's CHAT-SUMMARY for suggested title/excerpt/tags.
              let pTitle = '';
              let pExcerpt = '';
              let pTags = [];
              try {
                const lastMsg = Array.isArray(sess.messages) ? [...sess.messages].reverse().find((m) => m.role === 'assistant') : null;
                if (lastMsg && lastMsg.content) {
                  const parsed = splitDraftAndSummary(lastMsg.content);
                  pTitle = parsed.suggestedTitle || '';
                  pExcerpt = parsed.suggestedExcerpt || '';
                  pTags = Array.isArray(parsed.suggestedTags) ? parsed.suggestedTags : [];
                }
              } catch {}

              setForm((prev) => {
                const next = { ...prev };
                if (!next.title || !next.title.trim()) {
                  const t = pTitle || deriveTitleFromDraft(sess.last_proposed_draft);
                  if (t) next.title = t;
                }
                if (!next.slug || !next.slug.trim()) {
                  const base = next.title || pTitle || deriveTitleFromDraft(sess.last_proposed_draft);
                  if (base) next.slug = slugify(base);
                }
                if (!next.excerpt || !next.excerpt.trim()) {
                  const ex = pExcerpt || deriveExcerptFromDraft(sess.last_proposed_draft);
                  if (ex) next.excerpt = ex;
                }
                if ((!Array.isArray(next.tags) || next.tags.length === 0) && pTags && pTags.length) {
                  next.tags = [...pTags];
                }
                return next;
              });
              setSlugTouched(true);

              if (pTitle) setLastSuggestedTitle(pTitle);
              if (pExcerpt) setLastSuggestedExcerpt(pExcerpt);
              if (pTags && pTags.length) setLastSuggestedTags(pTags);
            }

            // Hydrate per-article research sources (type ResearchSource) captured via DuckDuckGo.
            // These are automatically saved to the authoring session for reference while writing.
            if (Array.isArray(sess.research_sources)) {
              setResearchSources(sess.research_sources);
            }
            if (sess.last_research_markdown) {
              setResearchMarkdown(sess.last_research_markdown);
            }
          }
        } catch (e) {
          // no session yet — OK
        }
      } catch (err) {
        toast.error('Failed to load post');
        navigate('/admin/dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, navigate]);

  // After any assistant turn completes (including free-form), keep chat lean:
  // - If the assistant output is long or contains the draft marker, extract the FULL draft for preview/diff.
  // - Replace the chat message with ONLY the short summary (metadata updates + change summary).
  // - This prevents huge payloads to /authoring-sessions (the 413 error) and keeps the Chat tab readable.
  useEffect(() => {
    if (chat.isStreaming) return;
    const msgs = chat.messages || [];
    if (msgs.length === 0) return;

    const lastIdx = msgs.length - 1;
    const last = msgs[lastIdx];
    if (!last || last.role !== 'assistant' || typeof last.content !== 'string') return;

    const content = last.content;
    if (content.trim().length < 200 && !content.includes(CHAT_SUMMARY_START)) return;

    const { full, summary, suggestedTitle: sTitle = '', suggestedExcerpt: sExcerpt = '', suggestedTags: sTagsFromSplit = [] } = splitDraftAndSummary(content);

    // Parse suggested tags from the summary block if present (local fallback + from split)
    let sTags = Array.isArray(sTagsFromSplit) ? [...sTagsFromSplit] : [];
    if (!sTags.length) {
      try {
        const tagMatch = (summary || content).match(/Suggested tags?:\s*([^\n]+)/i);
        if (tagMatch) {
          sTags = tagMatch[1]
            .split(/[,;]/)
            .map((t) => t.trim())
            .filter(Boolean)
            .slice(0, 10);
        }
      } catch {}
    }

    let didCollapse = false;
    const updated = [...msgs];

    if (summary && summary !== content) {
      updated[lastIdx] = { ...last, content: summary };
      chat.setMessages(updated);
      didCollapse = true;
    }

    const isDraftLike = full && (full.length > 600 || /\n##\s/.test(full) || full.startsWith('#') || full.includes('\n- '));
    if (isDraftLike && full !== proposedDraft) {
      setProposedDraft(full);
      setActiveTab(3);

      // Autofill EMPTY metadata fields only (title, slug, excerpt, tags).
      // Content is always replaced on approve.
      // Never overwrite fields that already have values.
      setForm((prev) => {
        const next = { ...prev };
        const wantTitle = !next.title || !next.title.trim();
        const wantSlug = !next.slug || !next.slug.trim();
        const wantExcerpt = !next.excerpt || !next.excerpt.trim();
        const wantTags = !Array.isArray(next.tags) || next.tags.length === 0;

        if (wantTitle) {
          const t = sTitle || deriveTitleFromDraft(full);
          if (t) next.title = t;
        }
        if (wantSlug) {
          const base = next.title || sTitle || deriveTitleFromDraft(full);
          if (base) next.slug = slugify(base);
        }
        if (wantExcerpt) {
          const ex = sExcerpt || deriveExcerptFromDraft(full);
          if (ex) next.excerpt = ex;
        }
        if (wantTags && sTags && sTags.length) {
          next.tags = [...sTags];
        }
        return next;
      });
      setSlugTouched(true);

      // Persist the full draft for resume, but the chat messages we persist will be the collapsed ones
      persistCurrentSession({ last_proposed_draft: full, messages: didCollapse ? updated : msgs });

      // Remember suggestions for later approve path (only used if fields remain empty)
      if (sTitle) setLastSuggestedTitle(sTitle);
      if (sExcerpt) setLastSuggestedExcerpt(sExcerpt);
      if (sTags && sTags.length) setLastSuggestedTags(sTags);

      // Also autofill tags at this moment if still empty
      setForm((prev) => {
        const next = { ...prev };
        const wantTags = !Array.isArray(next.tags) || next.tags.length === 0;
        if (wantTags && sTags && sTags.length) {
          next.tags = [...sTags];
        }
        return next;
      });
    } else if (didCollapse) {
      persistCurrentSession({ messages: updated });
    }

    if ((activeStage === 'draft' || activeStage === 'finalize')) {
      setActiveStage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.isStreaming, chat.messages.length]);

  // Auto-persist session after assistant turns complete
  // Persist only a sanitized (short) copy of messages so the payload stays small.
  const prevStreamingRef = useRef(false);
  useEffect(() => {
    const wasStreaming = prevStreamingRef.current;
    if (wasStreaming && !chat.isStreaming && chat.messages.length > 0) {
      const last = chat.messages[chat.messages.length - 1];
      if (last.role === 'assistant' && last.content) {
        // Sanitize before persisting to avoid 413 on authoring-sessions
        const sanitized = sanitizeMessagesForPersistence(chat.messages);
        persistCurrentSession({ messages: sanitized });
      }
    }
    prevStreamingRef.current = chat.isStreaming;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.isStreaming, persistCurrentSession]);

  const update = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'title' && !slugTouched) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    update('slug', slugify(value));
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (!tag) return;
    if (form.tags.includes(tag)) {
      setTagInput('');
      return;
    }
    if (form.tags.length >= 20) {
      toast.warning('Maximum 20 tags');
      return;
    }
    setForm((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
    setTagInput('');
  };

  const removeTag = (tag) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  // Stage action buttons: send a natural-language PREDEFINED PROMPT as a normal chat message
  // so every turn — button-triggered or free-typed — is always classified by the backend
  // planner (no client-side stage bypass). The wording is unambiguous enough that the
  // planner routes to the same agent with near-certainty, matching prior deterministic
  // button behavior. The planner/agents (research, metadata, etc.) run entirely server-side.
  const handleStageAction = async (stage) => {
    setActiveStage(stage);

    let instructions = '';
    if (stage === 'revise') {
      // Ask user for specific revise instructions via a quick prompt
      instructions = window.prompt('How should I revise the current content?', 'Make it tighter and add a practical example.');
      if (!instructions) {
        setActiveStage(null);
        return;
      }
    }

    // For draft and finalize, switch to the Differences tab proactively so user sees the unified diff
    if (stage === 'draft' || stage === 'finalize') {
      setActiveTab(3);
    }

    const topicForResearch = form.title || chat.messages.find((m) => m.role === 'user')?.content || 'the current article topic';
    const predefinedPrompts = {
      research: `Research this topic thoroughly and save sources for later reference: ${topicForResearch}`,
      outline: 'Generate a tight, publication-ready outline for this article.',
      draft: 'Write the full draft of this article now.',
      revise: `Revise the current draft: ${instructions}`,
      finalize: 'Finalize and polish the current draft for publishing.',
    };
    const text = predefinedPrompts[stage] || `Please help with: ${stage}`;

    await chat.sendMessage(text, { context: buildContext({ instructions }) });
  };

  // Approve the diff: replace editor content with the proposed draft
  // Also autofill any still-empty metadata (title, slug, excerpt, tags) from the approved draft.
  // Content is the only field we ALWAYS replace on approve.
  // Never overwrite a field that already has a value.
  const handleApproveDraft = (newContent) => {
    if (!newContent) return;
    setForm((prev) => {
      const next = { ...prev, content: newContent };
      // Autofill only if still empty — never overwrite a user-filled value
      if (!next.title || !next.title.trim()) {
        const t = lastSuggestedTitle || deriveTitleFromDraft(newContent);
        if (t) next.title = t;
      }
      if (!next.slug || !next.slug.trim()) {
        const base = next.title || lastSuggestedTitle || deriveTitleFromDraft(newContent);
        if (base) next.slug = slugify(base);
      }
      if (!next.excerpt || !next.excerpt.trim()) {
        const ex = lastSuggestedExcerpt || deriveExcerptFromDraft(newContent);
        if (ex) next.excerpt = ex;
      }
      if (!Array.isArray(next.tags) || next.tags.length === 0) {
        if (lastSuggestedTags && lastSuggestedTags.length) {
          next.tags = [...lastSuggestedTags];
        }
      }
      return next;
    });
    setSlugTouched(true);
    setProposedDraft(null);
    // After applying, show the rendered preview (Tab 2) so user sees the result
    setActiveTab(2);
    toast.success('Draft applied. Review in Article Preview and save when ready.');
    // Persist the fact that we accepted it
    persistCurrentSession({ last_proposed_draft: null });
  };

  const handleDiscardDraft = () => {
    setProposedDraft(null);
    setActiveStage(null);
  };

  // Heuristic: when an outline stage just completed, parse bullets from the last assistant message
  useEffect(() => {
    if (activeStage !== 'outline' || chat.isStreaming) return;
    const lastAssistant = [...chat.messages].reverse().find((m) => m.role === 'assistant');
    if (!lastAssistant || !lastAssistant.content) return;

    const lines = lastAssistant.content.split('\n')
      .map((l) => l.trim())
      .filter((l) => l.startsWith('- ') || l.startsWith('* '));

    if (lines.length >= 3) {
      const clean = lines.map((l) => l.replace(/^[-*]\s+/, '').trim()).filter(Boolean);
      setOutline(clean.slice(0, 30));
      persistCurrentSession({ outline: clean.slice(0, 30) });
      setActiveStage(null);
      toast.info('Outline updated from AI');
    }
  }, [chat.isStreaming, activeStage, chat.messages, persistCurrentSession]);

  // When we create a new post, link any existing unlinked session
  const linkSessionAfterCreate = async (newPostId) => {
    if (!sessionId || !newPostId) return;
    try {
      await adminApi.put(`/authoring-sessions/${sessionId}`, { linked_post_id: newPostId });
    } catch {}
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug || !form.excerpt.trim() || !form.content.trim()) {
      toast.error('Title, slug, excerpt, and content are required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await adminApi.put(`/blogs/${id}`, form);
        toast.success('Post updated');
      } else {
        const { data: created } = await adminApi.post('/blogs', form);
        toast.success('Post created');
        // If we have an in-progress AI session, link it to the new post
        if (sessionId && created?.id) {
          await linkSessionAfterCreate(created.id);
        }
        navigate('/admin/dashboard');
        return;
      }
      navigate('/admin/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Button
          component={Link}
          to="/admin/dashboard"
          variant="plain"
          size="sm"
          startDecorator={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to posts
        </Button>
        <Stack direction="row" spacing={1}>
          {form.slug && (
            <Button
              component={Link}
              to={`/blog/${form.slug}`}
              target="_blank"
              variant="outlined"
              size="md"
              startDecorator={<VisibilityIcon />}
            >
              Preview
            </Button>
          )}
          <Button
            size="md"
            startDecorator={<SaveIcon />}
            loading={saving}
            onClick={handleSave}
            sx={{
              background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 100%)',
              },
            }}
          >
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </Stack>
      </Box>

      <Typography
        level="h2"
        sx={{
          fontWeight: 700,
          mb: 4,
          background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 50%, #00A86B 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {isEdit ? 'Edit Post' : 'New Post'}
      </Typography>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        {/* 4-Tab UI: Chat | Metadata Form | Article Preview | Differences (unified diff) */}
        <Box sx={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ '--Tabs-gap': 0 }}>
            <TabList variant="outlined" sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <Tab>Chat</Tab>
              <Tab>Metadata Form</Tab>
              <Tab>Article Preview</Tab>
              <Tab>Differences</Tab>
              <Tab>Research{researchSources.length > 0 ? ` (${researchSources.length})` : ''}</Tab>
            </TabList>

            {/* Tab 0: Chat — every message (free text or a stage button's predefined prompt)
                is routed through the backend planner, which decides which agent(s) handle it. */}
            <TabPanel value={0} sx={{ p: 2 }}>
              <BlogChatPanel
                messages={chat.messages}
                isStreaming={chat.isStreaming}
                error={chat.error}
                plan={chat.plan}
                activeAgent={chat.activeAgent}
                onSend={(text) => chat.sendMessage(text, { context: buildContext() })}
                onStop={chat.stop}
                onRetry={chat.retry}
                onStageAction={handleStageAction}
                onProposeDraft={(content) => setProposedDraft(content)}
                disabled={saving || loading}
                researchCount={researchSources.length}
              />
            </TabPanel>

            {/* Tab 1: Metadata Form (canonical place for human edits) */}
            <TabPanel value={1} sx={{ p: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Stack spacing={2}>
                  <FormControl required>
                    <FormLabel sx={{ color: 'text.secondary', fontWeight: 600 }}>Title</FormLabel>
                    <Input
                      value={form.title}
                      onChange={(e) => update('title', e.target.value)}
                      placeholder="Building for Scale: Lessons from Three Years"
                      sx={editorFieldSx}
                    />
                  </FormControl>

                  <FormControl required>
                    <FormLabel sx={{ color: 'text.secondary', fontWeight: 600 }}>Slug</FormLabel>
                    <Input
                      value={form.slug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      placeholder="building-for-scale-lessons"
                      sx={editorFieldSx}
                    />
                  </FormControl>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <FormControl required>
                      <FormLabel sx={{ color: 'text.secondary', fontWeight: 600 }}>Date</FormLabel>
                      <Input type="date" value={form.date} onChange={(e) => update('date', e.target.value)} sx={editorFieldSx} />
                    </FormControl>
                    <FormControl>
                      <FormLabel sx={{ color: 'text.secondary', fontWeight: 600 }}>Author</FormLabel>
                      <Input value={form.author} onChange={(e) => update('author', e.target.value)} sx={editorFieldSx} />
                    </FormControl>
                  </Box>

                  <FormControl required>
                    <FormLabel sx={{ color: 'text.secondary', fontWeight: 600 }}>Excerpt</FormLabel>
                    <Textarea
                      value={form.excerpt}
                      onChange={(e) => update('excerpt', e.target.value)}
                      minRows={3}
                      maxRows={5}
                      placeholder="A short summary shown in blog listings."
                      sx={{ ...editorFieldSx, fontFamily: 'inherit' }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel sx={{ color: 'text.secondary', fontWeight: 600 }}>Tags</FormLabel>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                        placeholder="Add tag and press Enter"
                        sx={{ ...editorFieldSx, flex: 1 }}
                      />
                      <Button variant="outlined" onClick={addTag}>Add</Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {form.tags.map((tag) => (
                        <Chip
                          key={tag}
                          variant="soft"
                          onClick={() => removeTag(tag)}
                          sx={{ background: 'rgba(46, 91, 186, 0.1)', color: '#2E5BBA', cursor: 'pointer', '&:hover': { background: 'rgba(220, 53, 69, 0.15)', color: '#dc3545' } }}
                        >
                          {tag} ×
                        </Chip>
                      ))}
                    </Box>
                  </FormControl>

                  <FormControl>
                    <Switch
                      checked={form.published}
                      onChange={(e) => update('published', e.target.checked)}
                      endDecorator={<Typography level="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>{form.published ? 'Published (visible on site)' : 'Draft (hidden from site)'}</Typography>}
                    />
                  </FormControl>
                </Stack>

                {/* Outline points inside metadata tab for convenience */}
                <Box>
                  <FormLabel sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>Working Points / Outline</FormLabel>
                  <Box sx={{ p: 2, minHeight: 140, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
                    {outline.length === 0 && (
                      <Typography level="body3" sx={{ color: 'text.secondary', mb: 1, fontStyle: 'italic' }}>Use the Outline stage or add points manually.</Typography>
                    )}
                    <Stack spacing={0.75}>
                      {outline.map((item, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                          <Input
                            value={item}
                            onChange={(e) => { const next = [...outline]; next[i] = e.target.value; setOutline(next); }}
                            onBlur={() => persistCurrentSession({ outline })}
                            size="sm"
                            sx={{ flex: 1, fontSize: '0.9rem' }}
                          />
                          <Button size="sm" variant="plain" color="danger" onClick={() => { const next = outline.filter((_, idx) => idx !== i); setOutline(next); persistCurrentSession({ outline: next }); }}>×</Button>
                        </Box>
                      ))}
                    </Stack>
                    <Button size="sm" variant="outlined" sx={{ mt: 1 }} onClick={() => setOutline([...outline, ''])}>+ Add point</Button>
                  </Box>
                  <Typography level="body3" sx={{ color: 'text.secondary', mt: 1 }}>
                    These points are sent to the AI during Research to guide the article.
                  </Typography>
                </Box>
              </Box>

              {/* Full article body editor lives in the Metadata Form tab (canonical manual editing location) */}
              <FormControl required sx={{ mt: 3 }}>
                <FormLabel sx={{ color: 'text.secondary', fontWeight: 600 }}>Content (Markdown)</FormLabel>
                <Textarea
                  value={form.content}
                  onChange={(e) => update('content', e.target.value)}
                  minRows={18}
                  placeholder={'# Heading\n\nWrite your post in **markdown**...'}
                  sx={{
                    ...editorFieldSx,
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                    minHeight: 420,
                  }}
                />
              </FormControl>
              <Typography level="body3" sx={{ color: 'text.secondary', mt: 1 }}>
                Manual edits happen here. AI Draft/Finalize will propose changes visible in the Differences tab.
              </Typography>
            </TabPanel>

            {/* Tab 2: Article Preview (rendered) */}
            <TabPanel value={2} sx={{ p: 2 }}>
              <Typography level="h4" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>Preview</Typography>
              <Box sx={{ p: 4, background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 2, minHeight: 200 }}>
                {form.content ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {form.content}
                  </ReactMarkdown>
                ) : (
                  <Typography level="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Preview appears here (content from Metadata Form or AI draft after approval).</Typography>
                )}
              </Box>
              <Typography level="body3" sx={{ color: 'text.secondary', mt: 2 }}>
                This tab shows the current saved/edited content from Tab 2. After you Approve a draft, the content will appear here.
              </Typography>
            </TabPanel>

            {/* Tab 3: Differences — unified diff using existing DiffViewer (side-by-side for clarity) */}
            <TabPanel value={3} sx={{ p: 2 }}>
              {proposedDraft ? (
                <DiffViewer
                  current={form.content}
                  proposed={proposedDraft}
                  onApprove={handleApproveDraft}
                  onDiscard={handleDiscardDraft}
                  onCancel={() => setProposedDraft(null)}
                />
              ) : (
                <Box sx={{ p: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Typography level="body2" sx={{ color: 'text.secondary' }}>
                    No proposed draft yet. Use the “Draft” or “Finalize” buttons in the Chat tab to generate one. The diff will appear here.
                  </Typography>
                </Box>
              )}
            </TabPanel>

            {/* Tab 4: Research — live web research sources (type ResearchSource) captured via
                DuckDuckGo (US region). Automatically generated (button or free-text "research…"
                requests) and saved per-article so they can be referenced while writing. */}
            <TabPanel value={4} sx={{ p: 2 }}>
              <Typography level="h4" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
                Research Report
              </Typography>
              {researchSources.length === 0 ? (
                <Box sx={{ p: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Typography level="body2" sx={{ color: 'text.secondary' }}>
                    No research yet. Click “Research” in the Chat tab, or just type a request like
                    “Research why X increased in 2026” — the planner will route it to the Research
                    agent automatically and the report will appear here.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={3}>
                  <Box sx={{ p: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
                    <Typography level="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1.5 }}>
                      Sources ({researchSources.length})
                    </Typography>
                    <Stack spacing={1}>
                      {researchSources.map((s, i) => (
                        <Box key={i} sx={{ borderBottom: i < researchSources.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none', pb: 1 }}>
                          <Typography level="body2" sx={{ fontWeight: 600 }}>
                            <a href={s.url} target="_blank" rel="noreferrer" style={{ color: '#2E5BBA' }}>{s.title || s.url}</a>
                          </Typography>
                          {s.domain && (
                            <Typography level="body3" sx={{ color: 'text.secondary' }}>{s.domain}</Typography>
                          )}
                          {s.snippet && (
                            <Typography level="body3" sx={{ color: 'text.secondary', mt: 0.5 }}>{s.snippet}</Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  {researchMarkdown && (
                    <Box sx={{ p: 3, background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 2 }}>
                      <Typography level="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1.5 }}>
                        Full Report (saved for reference while writing)
                      </Typography>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {researchMarkdown}
                      </ReactMarkdown>
                    </Box>
                  )}
                </Stack>
              )}
            </TabPanel>
          </Tabs>
        </Box>
      </motion.div>
    </Box>
  );
};

const editorFieldSx = {
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  '&:focus-within': {
    border: '1px solid #2E5BBA',
  },
};

export default AdminBlogEditor;
